/**
 * Feuille « Aller à un point » — EF-RBK-010.
 *
 * Elle est atteinte en deux gestes par le bouton d'actions, permanent et
 * explicitement libellé. Une action sans cible en aval reste visible mais
 * désactivée, avec sa raison : l'inventaire des commandes ne varie pas.
 */

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { CATEGORIES, categoryLabel } from '../../core/categories';
import type { PoiId } from '../../core/model';
import { findNextPoiRow, findRowAtProgression } from '../../roadbook/display';
import { ACTIONS, SHEETS } from '../labels';
import { Sheet } from './Sheet';
import { closeSheet, displayRows, document_, goToRow, parameters } from '../state';

export function GotoSheet(): JSX.Element {
  const current = document_.value;
  const rows = displayRows.value;
  const [kilometer, setKilometer] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (current === null) {
    return (
      <Sheet title={SHEETS.goto} onClose={closeSheet}>
        <p>Aucun roadbook chargé.</p>
      </Sheet>
    );
  }

  const maximum = current.route.track.lengthMeters / 1000;
  const pois = current.pois;
  const active = parameters.value.search.categories;

  const submit = (): void => {
    const normalized = kilometer.replace(',', '.').trim();
    if (normalized.length === 0) {
      setError('Saisissez un kilomètre.');
      return;
    }
    const value = Number.parseFloat(normalized);
    if (!Number.isFinite(value) || value < 0 || value > maximum) {
      setError(`Saisissez une valeur comprise entre 0 et ${maximum.toFixed(1).replace('.', ',')}.`);
      return;
    }
    setError(null);
    goToRow(findRowAtProgression(rows, value * 1000));
  };

  const nextMatching = (matches: (poiId: PoiId) => boolean): number =>
    findNextPoiRow(rows, -1, (ref) => matches(ref.poiId));

  const nextPinned = nextMatching((id) => pois.get(id)?.userState.pinned === true);

  return (
    <Sheet title={SHEETS.goto} onClose={closeSheet}>
      <label>
        <span class="field-label">Aller au kilomètre (0 à {maximum.toFixed(1).replace('.', ',')})</span>
        <input
          type="text"
          inputMode="decimal"
          value={kilometer}
          aria-invalid={error !== null}
          onInput={(event) => {
            setKilometer((event.currentTarget as HTMLInputElement).value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
        {error !== null ? <span class="field-error">{error}</span> : null}
      </label>

      <button type="button" class="primary wide" onClick={submit}>
        {ACTIONS.goToKilometer}
      </button>

      <div class="actions">
        <button
          type="button"
          onClick={() => {
            goToRow(0);
          }}
        >
          {ACTIONS.goToStart}
        </button>
        <button
          type="button"
          onClick={() => {
            goToRow(rows.length - 1);
          }}
        >
          {ACTIONS.goToFinish}
        </button>
        <button
          type="button"
          disabled={nextPinned < 0}
          title={nextPinned < 0 ? 'aucun POI épinglé en aval' : undefined}
          onClick={() => {
            goToRow(nextPinned);
          }}
        >
          {ACTIONS.goToNextPinned}
        </button>
      </div>

      <h3 class="sheet__group">Aller au prochain POI de la catégorie</h3>
      <div class="actions">
        {CATEGORIES.filter((definition) => active.includes(definition.key)).map((definition) => {
          const index = nextMatching((id) => pois.get(id)?.categories.includes(definition.key) === true);
          return (
            <button
              key={definition.key}
              type="button"
              disabled={index < 0}
              title={index < 0 ? 'aucun POI de cette catégorie en aval' : undefined}
              onClick={() => {
                goToRow(index);
              }}
            >
              {categoryLabel(definition.key)}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}
